import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { authScopes, protectedResources } from "../../authConfig";
import { callApi } from "../../helpers/call_api";
import { getIdToken } from "../../helpers/msal_auth";
import { RootState } from "../store";
import { InteractionRequiredAuthError } from "@azure/msal-common";

// Define the Secret interface
export interface Secret {
  secret_name: string;
  tenant_name?: string;
  tenant_ID?: string;
  arn?: string;
  expiry_date?: string;
  daysUntilExpiry?: number;
  daysExpired?: number;
  isExpired?: boolean;
}

export interface SecretUpdatePayload {
  credentialType: string;
  accessKey?: string;
  secretKey?: string;
  expiryDate?: string;
}

// Define the state structure
export interface SecretsState {
  secrets: Secret[];
  selectedSecret: Secret | null;
  isLoading: boolean;
  error: any;
  count: number;
  tenantId: string | null;
  updateSuccess: boolean;
  updateMessage: string | null;
}

// Initial state
const initialState: SecretsState = {
  secrets: [],
  selectedSecret: null,
  isLoading: false,
  error: {},
  count: 0,
  tenantId: null,
  updateSuccess: false,
  updateMessage: null
};

// Create async thunk for fetching all secrets
export const getSecretsAsync = createAsyncThunk(
  "secrets/getSecrets",
  async (_, { rejectWithValue }) => {
    try {
      const idToken = await getIdToken(authScopes);
      const response = await callApi(
        idToken,
        protectedResources.supportApi + "/admin/secrets",
        "GET"
      );

      return response;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        return rejectWithValue({
          error: "Authentication required. Please sign in again."
        });
      }
      return rejectWithValue({
        error: e instanceof Error ? e.message : "Failed to fetch secrets"
      });
    }
  }
);

// Create async thunk for fetching a specific secret
export const getSecretByNameAsync = createAsyncThunk(
  "secrets/getSecretByName",
  async (secretName: string, { rejectWithValue }) => {
    try {
      const idToken = await getIdToken(authScopes);
      const response = await callApi(
        idToken,
        `${protectedResources.supportApi}/admin/secret?secretName=${encodeURIComponent(secretName)}`,
        "GET"
      );

      return response;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        return rejectWithValue({
          error: "Authentication required. Please sign in again."
        });
      }
      return rejectWithValue({
        error: e instanceof Error ? e.message : "Failed to fetch secret details"
      });
    }
  }
);

// Create async thunk for updating secrets
export const updateSecretAsync = createAsyncThunk(
  "secrets/updateSecret",
  async (payload: SecretUpdatePayload, { rejectWithValue }) => {
    try {
      const idToken = await getIdToken(authScopes);
      const response = await callApi(
        idToken,
        `${protectedResources.supportApi}/admin/secrets/update`,
        "POST",
        payload
      );

      return {
        ...response,
        credentialType: payload.credentialType
      };
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        return rejectWithValue({
          error: "Authentication required. Please sign in again."
        });
      }
      return rejectWithValue({
        error: e instanceof Error ? e.message : "Failed to update secret"
      });
    }
  }
);

// Create the secrets slice
const secretsSlice = createSlice({
  name: "secrets",
  initialState,
  reducers: {
    clearSecrets: (state) => {
      state.secrets = [];
      state.count = 0;
      state.error = {};
    },
    clearSelectedSecret: (state) => {
      state.selectedSecret = null;
    },
    clearUpdateStatus: (state) => {
      state.updateSuccess = false;
      state.updateMessage = null;
      state.error = {};
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle all secrets fetch
      .addCase(getSecretsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = {};
      })
      .addCase(getSecretsAsync.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.secrets = action.payload.secrets || [];
        state.count = action.payload.count || 0;
        state.tenantId = action.payload.tenantId || null;
      })
      .addCase(getSecretsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || { error: "Unknown error occurred" };
      })

      // Handle single secret fetch
      .addCase(getSecretByNameAsync.pending, (state) => {
        state.isLoading = true;
        state.error = {};
      })
      .addCase(getSecretByNameAsync.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.selectedSecret = action.payload;

      })
      .addCase(getSecretByNameAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || { error: "Unknown error occurred" };
      })

      // Handle secret update
      .addCase(updateSecretAsync.pending, (state) => {
        state.isLoading = true;
        state.error = {};
        state.updateSuccess = false;
        state.updateMessage = null;
      })
      .addCase(updateSecretAsync.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.updateSuccess = true;

        if (action.payload.credentialType === 'graphapi') {
          state.updateMessage = `Successfully updated GraphAPI credentials`;
        } else {
          state.updateMessage = `Successfully submitted update request`;
        }
      })
      .addCase(updateSecretAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.updateSuccess = false;
        state.error = action.payload || { error: "Unknown error occurred while updating the secret" };
      });
  }
});

export const { clearSecrets, clearSelectedSecret, clearUpdateStatus } = secretsSlice.actions;
export const selectSecrets = (state: RootState) => state.secrets?.secrets || [];
export const selectSelectedSecret = (state: RootState) => state.secrets?.selectedSecret || null;
export const selectSecretsLoading = (state: RootState) => state.secrets?.isLoading || false;
export const selectSecretsError = (state: RootState) => state.secrets?.error || {};
export const selectSecretsCount = (state: RootState) => state.secrets?.count || 0;
export const selectTenantId = (state: RootState) => state.secrets?.tenantId || null;
export const selectUpdateSuccess = (state: RootState) => state.secrets?.updateSuccess || false;
export const selectUpdateMessage = (state: RootState) => state.secrets?.updateMessage || null;

export default secretsSlice.reducer;